import { config } from '../config/environment';
import { ROUTES } from '../constants/routes';

const API_BASE_URL = config.api.baseUrl;

/**
 * Get authentication headers for API calls
 * @param {string} token - JWT session token from Clerk
 * @returns {Object} Headers with authentication
 */
function getAuthHeaders(token) {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Enhanced publishing API with authentication and validation handling
 */
export const publishingApi = {
  /**
   * Publish a room - creates a published version with unique ID
   * @param {string} roomId - Editor room ID
   * @param {Object} roomData - Room configuration and models
   * @param {string} token - JWT session token from Clerk
   * @returns {Promise<Object>} Publishing result with publishedId
   */
  async publishRoom(roomId, roomData, token = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/rooms/${roomId}/publish`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ roomData }),
      });

      const result = await response.json();

      if (response.status === 403) {
        // Content validation blocked the publishing
        return {
          success: false,
          error: {
            message: result.error?.message || 'Room cannot be published due to content validation.',
            code: 'PUBLISHING_BLOCKED',
            details: result.error?.details || {},
            requiresReview: true
          }
        };
      }

      if (!response.ok) {
        throw new Error(result.error?.message || 'Publishing failed');
      }

      // Generate publishedUrl from the response
      const publishedUrl = `${window.location.origin}${ROUTES.PUBLISHED(result.data.publishedId)}`;

      return {
        success: true,
        data: {
          ...result.data,
          publishedUrl
        }
      };
    } catch (error) {
      console.error('Publishing API error:', error);
      return {
        success: false,
        error: {
          message: error.message || 'Publishing failed',
          code: 'PUBLISH_ERROR'
        }
      };
    }
  },

  /**
   * Get publishing status for a room
   * @param {string} roomId - Editor room ID
   * @returns {Promise<Object>} Current publishing status
   */
  async getPublishingStatus(roomId) {
    try {
      const response = await fetch(`${API_BASE_URL}/rooms/${roomId}/publish-status`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Failed to get publishing status');
      }

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('Get publishing status error:', error);
      return {
        success: false,
        error: {
          message: error.message,
          code: 'STATUS_ERROR'
        }
      };
    }
  },

  /**
   * Get published room data (for player mode)
   * @param {string} publishedId - Published room ID
   * @returns {Promise<Object>} Published room data
   */
  async getPublishedRoom(publishedId) {
    try {
      const response = await fetch(`${API_BASE_URL}/published-rooms/${publishedId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Published room not found');
      }

      // Transform the data format to match what PlayerPage expects
      const publishedRoom = result.data;
      return {
        success: true,
        data: {
          publishedId: publishedRoom.publishedId,
          publishedAt: publishedRoom.publishedAt,
          creatorUsername: publishedRoom.creatorUsername || publishedRoom.roomData?.creatorUsername || null,
          creatorDisplayName: publishedRoom.creatorDisplayName || publishedRoom.roomData?.creatorDisplayName || null,
          // Transform room data to frontend format
          ...publishedRoom.roomData
        }
      };
    } catch (error) {
      console.error('Get published room error:', error);
      return {
        success: false,
        error: {
          message: error.message,
          code: 'ROOM_NOT_FOUND'
        }
      };
    }
  },

  /**
   * Re-publish a room - updates existing published room
   * @param {string} roomId - Editor room ID
   * @param {Object} roomData - Updated room configuration and models
   * @param {string} token - JWT session token from Clerk
   * @returns {Promise<Object>} Re-publishing result
   */
  async republishRoom(roomId, roomData, token = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/rooms/${roomId}/republish`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ roomData }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || 'Re-publishing failed');
      }

      return {
        success: true,
        data: result.data
      };
    } catch (error) {
      console.error('Re-publishing API error:', error);
      return {
        success: false,
        error: {
          message: error.message || 'Re-publishing failed',
          code: 'REPUBLISH_ERROR'
        }
      };
    }
  },

  /**
   * Verify password for a published room
   * @param {string} publishedId - Published room ID
   * @param {string} password - Password to verify
   * @returns {Promise<Object>} Published room data if password is correct
   */
  async verifyPublishedRoomPassword(publishedId, password) {
    try {
      const response = await fetch(`${API_BASE_URL}/published-rooms/${publishedId}/verify-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            message: result.error?.message || 'Incorrect password',
            code: result.error?.code || 'INVALID_PASSWORD'
          }
        };
      }

      const publishedRoom = result.data;
      return {
        success: true,
        data: {
          publishedId: publishedRoom.publishedId,
          publishedAt: publishedRoom.publishedAt,
          ...publishedRoom.roomData
        }
      };
    } catch (error) {
      console.error('Verify published room password error:', error);
      return {
        success: false,
        error: {
          message: error.message || 'Failed to verify password',
          code: 'VERIFY_ERROR'
        }
      };
    }
  }
};

export default publishingApi;
